import { useEffect, useState, useRef, useCallback } from 'react';

interface UseTableHeightOptions {
  /**
   * Ref to the container element that wraps the table
   * If not provided, will measure from viewport
   */
  containerRef?: React.RefObject<HTMLElement>;
  /**
   * Refs to elements that take up fixed space (header, filters, pagination, etc.)
   */
  fixedHeightRefs?: {
    header?: React.RefObject<HTMLElement>;
    filters?: React.RefObject<HTMLElement>;
    pagination?: React.RefObject<HTMLElement>;
  };
  /**
   * Fixed heights to subtract from available space (header, filters, pagination, etc.)
   * in pixels. Used as fallback if refs are not available.
   */
  fixedHeights?: {
    header?: number;
    filters?: number;
    pagination?: number;
    padding?: number;
  };
  /**
   * Minimum table height in pixels
   */
  minHeight?: number;
  /**
   * Maximum table height in pixels or viewport height percentage (e.g., '80vh')
   */
  maxHeight?: number | string;
}

/**
 * Hook to calculate available height for a table based on viewport dimensions
 * and fixed elements (headers, filters, pagination, etc.)
 */
export function useTableHeight({
  containerRef,
  fixedHeightRefs,
  fixedHeights = {},
  minHeight = 300,
  maxHeight = '90vh',
}: UseTableHeightOptions = {}) {
  const [tableHeight, setTableHeight] = useState<number>(minHeight);
  const measureRef = useRef<HTMLElement | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);

  const calculateHeight = useCallback(() => {
    let availableHeight: number;

    if (containerRef?.current) {
      // Measure from container element
      const containerRect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      availableHeight = viewportHeight - containerRect.top;
    } else if (measureRef.current) {
      // Measure from the element itself
      const rect = measureRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      availableHeight = viewportHeight - rect.top;
    } else {
      // Fallback to viewport height
      availableHeight = window.innerHeight;
    }

    // Get heights from refs if available, otherwise use fixed heights
    const headerHeight = fixedHeightRefs?.header?.current?.offsetHeight || fixedHeights.header || 0;
    const filtersHeight = fixedHeightRefs?.filters?.current?.offsetHeight || fixedHeights.filters || 0;
    const paginationHeight = fixedHeightRefs?.pagination?.current?.offsetHeight || fixedHeights.pagination || 0;
    const padding = fixedHeights.padding || 0;

    const calculatedHeight = availableHeight - headerHeight - filtersHeight - paginationHeight - padding;

    // Apply min/max constraints
    let finalHeight = Math.max(minHeight, calculatedHeight);

    // Handle maxHeight (can be number or viewport height string like '90vh')
    if (maxHeight) {
      if (typeof maxHeight === 'string' && maxHeight.endsWith('vh')) {
        const vhPercentage = parseFloat(maxHeight) / 100;
        const maxHeightPx = window.innerHeight * vhPercentage;
        finalHeight = Math.min(finalHeight, maxHeightPx);
      } else if (typeof maxHeight === 'number') {
        finalHeight = Math.min(finalHeight, maxHeight);
      }
    }

    setTableHeight(finalHeight);
  }, [containerRef, fixedHeightRefs, fixedHeights, minHeight, maxHeight]);

  // Debounced version: coalesces rapid ResizeObserver callbacks (e.g. during CSS transitions)
  // into a single recalculation after the transition settles.
  const debouncedCalculateHeight = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    debounceTimerRef.current = setTimeout(() => {
      // Cancel any lingering RAF before scheduling a new one
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(calculateHeight);
    }, 60);
  }, [calculateHeight]);

  useEffect(() => {
    // Initial calculation with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(calculateHeight, 0);

    // Recalculate on window resize (debounced to avoid rapid updates)
    window.addEventListener('resize', debouncedCalculateHeight);

    // Use a single ResizeObserver for all elements — the browser batches
    // all size changes into one callback, avoiding per-element overhead.
    const observer = new ResizeObserver(debouncedCalculateHeight);

    if (containerRef?.current) {
      observer.observe(containerRef.current);
    }

    if (fixedHeightRefs) {
      Object.values(fixedHeightRefs).forEach((ref) => {
        if (ref?.current) {
          observer.observe(ref.current);
        }
      });
    }

    return () => {
      clearTimeout(timeoutId);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', debouncedCalculateHeight);
      observer.disconnect();
    };
  }, [calculateHeight, debouncedCalculateHeight, containerRef, fixedHeightRefs]);

  return { tableHeight, measureRef };
}
